import type { TExample } from '@atlas/uicorn/preview/modules';
import { useMemo } from 'react';
import * as React from 'react';
import { useTheme } from 'styled-components';
import useRefCallback from 'use-ref-callback';
import Tooltip, { type TTooltipProps } from '@atlas/uicorn/lib/v2/Tooltip';
import ChevronIcon from '@atlas/uicorn/lib/Icon/ChevronIcon';
import MoonIcon from '@atlas/uicorn/lib/Icon/MoonIcon';
import Spinner from '@atlas/uicorn/lib/Display/Spinner';
import window from '@atlas/tool/box/browser/window';
import type { TThemeColor } from '@atlas/uicorn/lib/System/StylesProvider';
import cn from 'classnames';
import styles from './Option.module.css';

const Option = React.memo(
    React.forwardRef<TOptionRef, TOptionProps>(function Option(props, ref): React.ReactElement {
        const {
            elementAs,
            type = 'button',
            noFocusStyle,
            noHoverStyle,
            noDisabledStyle,
            loading,
            ellipsis,
            danger = false,
            prefix,
            pushPrefix,
            suffix,
            pushSuffix,
            flex = 'start',
            children,
            tooltip,
            onClick,
            onMouseDown,
            propagate = false,
            delayClick = 0,
            href,
            hrefBlank,
            color,
            className,
            style,
            ...elementProps
        } = props;

        const handleClick = useRefCallback(function preventClickPropagation(event) {
            if (loading) return;

            // TODO: Maybe preventing default is good enough?
            if (!propagate) event.stopPropagation();
            try {
                onClick?.(event);
            } catch (error) {
                console.error(error);
            }
        });

        const handleClickCapture = useRefCallback<React.MouseEventHandler<HTMLButtonElement>>(
            function delayOrRejectEventIfNeeded(event) {
                const isDelayed = (event.nativeEvent as { __delayed?: boolean }).__delayed;

                if (isDelayed) return;

                if (delayClick < 0) {
                    // Event triggered by clipboard have this attributes equal to 0 and shouldn't be ignored
                    if (event.clientX || event.clientY) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                    return;
                }

                if (delayClick === 0) return;

                const { target } = event;
                if (!(target instanceof Node)) return;

                const clonedEvent = new MouseEvent('click', event.nativeEvent);
                Object.assign(clonedEvent, { __delayed: true });
                event.nativeEvent.stopPropagation();
                event.nativeEvent.preventDefault();

                setTimeout(() => {
                    if (window?.document.contains(target)) {
                        target.dispatchEvent(clonedEvent);
                    }
                }, delayClick);
            },
        );

        // TODO: With delayClick < 0 keydown with Enter and Space may also react faster
        const handleMouseDown = useRefCallback<React.MouseEventHandler<HTMLButtonElement>>(
            function mapEventToClickIfNeeded(event) {
                onMouseDown?.(event);
                if (event.defaultPrevented) return;

                if (delayClick >= 0) return;

                const { target } = event;
                if (!(target instanceof Node)) return;
                if (!window?.document.contains(target)) return;

                const clonedEvent = new MouseEvent('click', event.nativeEvent);
                Object.assign(clonedEvent, { __delayed: true });
                target.dispatchEvent(clonedEvent);
            },
        );

        const disabled = typeof props.disabled === 'boolean' ? props.disabled : !!props.loading;

        const tooltipProps = useMemo<undefined | Omit<TTooltipProps, 'children'>>(
            function normalizeTooltipProps() {
                if (!tooltip) return undefined;

                if (typeof tooltip === 'object') return tooltip;
                if (typeof tooltip === 'function') return { content: tooltip as () => React.ReactElement };

                return { content: <span>{tooltip}</span> };
            },
            [tooltip],
        );

        const theme = useTheme();

        const classNames = cn(styles.option, className, {
            [styles.flexStart]: flex === 'start',
            [styles.flexEnd]: flex === 'end',
            [styles.disabledStyle]: !noDisabledStyle,
            [styles.focusStyle]: !noFocusStyle,
            [styles.hoverStyle]: !noHoverStyle,
        });

        const inlineStyles: TOptionInlineStyles = {
            ...style,
        };

        if (danger) {
            inlineStyles['--option-text-color'] = theme.color.red6;
        } else if (color) {
            inlineStyles['--option-text-color'] = theme.color[color];
        }

        // The `as` attribute should be used wisely
        // I mute it here not to have type conflicts with reference and event handlers types
        const transformingOptionAs = elementAs ?? href ? 'a' : undefined;
        const transformingOptions: any =
            transformingOptionAs === 'a'
                ? { as: transformingOptionAs, href, target: hrefBlank ? '_blank' : '_self' }
                : { as: transformingOptionAs };
        if (href && elementAs && elementAs !== 'a') {
            console.error('Buttons that are using href parameter should not use elementAs parameter');
        }

        return (
            <Tooltip {...tooltipProps}>
                <OptionElement
                    role="button"
                    tabIndex={0}
                    {...elementProps}
                    type={type}
                    disabled={disabled}
                    onClick={handleClick}
                    onClickCapture={handleClickCapture}
                    onMouseDown={handleMouseDown}
                    {...transformingOptions}
                    ref={ref}
                    className={classNames}
                    style={inlineStyles}
                    data-ui-role="option"
                >
                    {(prefix || loading === 'prefix') && (
                        <span
                            className={cn(styles.optionAppendix, { [styles.right]: pushPrefix })}
                            data-ui-role="option-appendix"
                        >
                            {loading === 'prefix' ? (
                                <Spinner
                                    className={cn({
                                        [styles.spinnerAnimate]: !prefix,
                                    })}
                                />
                            ) : (
                                prefix
                            )}
                        </span>
                    )}
                    {children && ellipsis ? (
                        <span className={styles.ellipsis}>{children}</span>
                    ) : children && typeof children === 'string' ? (
                        <span>{children}</span>
                    ) : (
                        children
                    )}
                    {(suffix || loading === 'suffix') && (
                        <span className={cn(styles.optionAppendix, { [styles.left]: pushSuffix })}>
                            {loading === 'suffix' ? (
                                <Spinner
                                    className={cn({
                                        [styles.spinnerAnimate]: !suffix,
                                    })}
                                />
                            ) : (
                                suffix
                            )}
                        </span>
                    )}
                </OptionElement>
            </Tooltip>
        );
    }),
);

export default Option;

type TOptionElementProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
    React.HTMLAttributes<HTMLSpanElement> &
    React.AnchorHTMLAttributes<HTMLAnchorElement> & {
        as?: OptionElement;
    };

const OptionElement = React.forwardRef<HTMLElement, TOptionElementProps>(function OptionElement(props, ref) {
    if (props.as === 'a') {
        return <a {...props} ref={ref as React.Ref<HTMLAnchorElement>} />;
    }

    if (props.as === 'span') {
        return <span {...props} ref={ref as React.Ref<HTMLSpanElement>} />;
    }

    return <button {...props} ref={ref as React.Ref<HTMLButtonElement>} />;
});

// #region Types

export type TOptionFlex = 'center' | 'start' | 'end';

export type OptionElement = 'button' | 'a' | 'span';

export type TOptionPropsTooltip = string | number | (() => React.ReactNode) | Omit<TTooltipProps, 'children'>;

export type TOptionProps = Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'prefix' | 'children' | 'onClickCapture'
> & {
    elementAs?: OptionElement;
    noFocusStyle?: boolean;
    noHoverStyle?: boolean;
    noDisabledStyle?: boolean;
    loading?: false | null | 'prefix' | 'suffix';
    danger?: boolean;
    prefix?: React.ReactNode;
    pushPrefix?: boolean;
    suffix?: React.ReactNode;
    pushSuffix?: boolean;
    flex?: TOptionFlex;
    propagate?: boolean;
    delayClick?: number;
    tooltip?: TOptionPropsTooltip;
    [dataAttribute: `data-${string}`]: string;
    href?: string; // Will use <a> tag unless other is not specified with [elementAs]
    hrefBlank?: boolean; // Will add target="_blank" for anchor tag
    color?: TThemeColor;
} & ({ ellipsis?: false; children?: React.ReactNode } | { ellipsis?: true; children?: string | number });

export type TOptionRef = HTMLElement;

export type TOptionInlineStyles = React.CSSProperties & {
    '--option-text-color'?: string;
};

// #endregion

// #region Examples

export const Example: TExample = {
    containerStyle: {
        border: '1px dashed green',
    },
    render: ({ width, withTooltip, delayClick, link, ...props }) => {
        const style = width > 0 ? { width: `${width / 10}rem` } : undefined;

        const tooltip = withTooltip
            ? () => (
                  <span>
                      Refresh page <kbd>⌘R</kbd>
                  </span>
              )
            : undefined;

        return (
            <Option
                style={style}
                tooltip={tooltip}
                {...props}
                href={link ? '#' : undefined}
                delayClick={parseFloat(delayClick) || 0}
            />
        );
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=4-1064&t=N252bJVpMS7uwjzX-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-7700&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-6969&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=28-6083&mode=design&t=TVklSB9u5fm4OQmw-0',
    ],
    controls: {
        children: { type: 'string', initial: 'Press me' },
        disabled: 'boolean',
        loading: ['none', 'prefix', 'suffix'],
        width: { type: 'number', initial: 200, connected: false },
        prefix: { type: 'string', initial: '🦄', connected: false },
        pushPrefix: 'boolean',
        suffix: { type: 'string', initial: '→', connected: false },
        pushSuffix: 'boolean',
        flex: ['center', 'start', 'end'],
        withTooltip: 'boolean',
        delayClick: ['-10', 'none (0)', '-1', '100'],
        link: 'boolean',
    },
    handlers: {
        onClick: 'console',
    },
};

export const AllVariantsExample: TExample = {
    layout: 'column',
    render(props) {
        const groupStyle: React.CSSProperties = { display: 'flex', gap: '0.5rem' };
        const wideStyle: React.CSSProperties = { width: '30rem' };
        return (
            <>
                <Option {...props}>Basic</Option>
                <Option {...props} href="#">
                    Basic link
                </Option>
                <Option {...props}>
                    Rich <strong>content</strong>
                </Option>
                <Option {...props} disabled>
                    Disabled
                </Option>
                <Option {...props} loading="prefix">
                    Loading=prefix
                </Option>
                <Option {...props} loading="prefix" disabled={false}>
                    Loading=prefix & not disabled
                </Option>
                <Option {...props} prefix={<MoonIcon />}>
                    Prefixed
                </Option>
                <Option {...props} suffix={<ChevronIcon />}>
                    Suffixed
                </Option>
                <Option {...props} prefix="🦄" suffix="→">
                    Prefixed & suffixed with strings
                </Option>
                <Option {...props} loading="prefix" prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed & loading=prefix
                </Option>
                <Option {...props} loading="suffix" prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed & loading=suffix
                </Option>
                <div style={groupStyle}>
                    <Option {...props} loading="prefix" />
                    <Option {...props} prefix={<MoonIcon />} />
                    <Option {...props} loading="prefix" suffix={<ChevronIcon />} />
                </div>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Long text that wraps when there is not enough space
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />} ellipsis>
                    Option with the text that is cut with ellipsis
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} flex="start">
                    Wide & prefixed & flex start
                </Option>
                <Option {...props} style={wideStyle} suffix={<ChevronIcon />} flex="end">
                    Wide & suffixed & flex end
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} pushPrefix suffix={<ChevronIcon />}>
                    Wide & pushed prefix & suffix
                </Option>
                <Option {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />} pushSuffix>
                    Wide & prefix & pushed suffix
                </Option>
                <Option
                    {...props}
                    style={wideStyle}
                    suffix={
                        <>
                            <MoonIcon />
                            <ChevronIcon />
                        </>
                    }
                    pushSuffix
                >
                    Two actions on the right
                </Option>
                <Option {...props} style={wideStyle} suffix={<ChevronIcon />} pushSuffix href="#">
                    Link with action on the right
                </Option>
            </>
        );
    },
    handlers: {
        onClick: 'console',
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=4-1064&t=N252bJVpMS7uwjzX-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-7700&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-6969&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=28-6083&mode=design&t=TVklSB9u5fm4OQmw-0',
    ],
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Option', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Option>Press</Option>);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
